import {Box, Typography} from '@mui/material';
import {GridColumns} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {FormattedMessage, IntlShape} from 'react-intl';
import {formatPrice} from 'src/utils/helper';
import {getEvDriverNameFromMinimal} from 'src/utils/userNameFormatter';
import {ClientType} from 'src/types/transactions';

const colTitleSx = {
    width: '100%',
    textAlign: 'left',
    textTransform: 'uppercase',
    fontSize: '0.625rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const tableCellSx = {
    color: 'text.primary',
    fontSize: '0.75rem',
};

const getCompanyName = (user: any): string => {
    if (user?.userType === 'PRIVATE_DRIVER') {
        return '-';
    }
    if (user?.userType === 'COMPANY' && user?.company?.name) {
        return user.company.name;
    }
    if (user?.userType === 'EMPLOYEE' && user?.employee?.company?.name) {
        return user.employee.company.name;
    }
    return '-';
};

export const chargingSessionsTableColumns = (
    businessArr: GridColumns,
    intl: IntlShape
): GridColumns => [
    ...businessArr,
    {
        field: intl.formatMessage({id: 'locationNameColumnHeader'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="locationNameColumnHeader"/>
            </Box>
        ),
        flex: 1.3,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {params?.row?.location?.name || '-'}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'chargePoint'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="chargePoint"/>
            </Box>
        ),
        flex: 0.7,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {params?.row?.connector?.chargePointId || '-'}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'evDriverName'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="evDriverName"/>
            </Box>
        ),
        flex: 0.9,
        minWidth: 50,
        renderCell: (params) => {
            const isGuest = params?.row?.clientType === ClientType.GUEST;
            return (
                <Typography
                    variant="caption"
                    sx={tableCellSx}
                >
                    {isGuest ? 'GUEST' : getEvDriverNameFromMinimal(params?.row?.user)}
                </Typography>
            );
        },
    },
    {
        field: intl.formatMessage({id: 'company'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="company"/>
            </Box>
        ),
        flex: 0.9,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {getCompanyName(params?.row?.user)}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'idTag'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="rfid"/>
            </Box>
        ),
        flex: 0.8,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {params?.row?.idTag?.idTag || '-'}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'licensePlate'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="licensePlate"/>
            </Box>
        ),
        flex: 0.6,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {params?.row?.idTag?.licensePlate || '—'}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'transactionStart'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="transactionStart"/>
            </Box>
        ),
        flex: 0.85,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {dayjs(params?.row?.startTimestamp).format('DD MMM YYYY HH:mm:ss')}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'transactionEnd'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="transactionEnd"/>
            </Box>
        ),
        flex: 0.85,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {params?.row?.stopTimestamp
                    ? dayjs(params?.row?.stopTimestamp).format('DD MMM YYYY HH:mm:ss')
                    : '-'}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'powerConsumed'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="powerConsumed"/>
            </Box>
        ),
        flex: 0.8,
        minWidth: 50,
        renderCell: (params) => (
            <Typography
                variant="caption"
                sx={tableCellSx}
            >
                {`${((params?.row?.transactionSummary?.wattsConsumed || 0) / 1000).toFixed(3)} ${intl.formatMessage({id: 'kWh'})}`}
            </Typography>
        ),
    },
    {
        field: intl.formatMessage({id: 'cost'}),
        renderHeader: () => (
            <Box sx={colTitleSx}>
                <FormattedMessage id="costColumnHeader"/>
            </Box>
        ),
        flex: 0.7,
        minWidth: 50,
        renderCell: (params) => {
            const cost = params.row?.cost;
            if (cost?.totalCost !== undefined && cost?.totalCost !== null) {
                const amount = formatPrice(cost.totalCost);
                const currency = cost.currency || 'CHF';
                return (
                    <Typography
                        variant="caption"
                        sx={tableCellSx}
                    >
                        {`${amount} ${currency}`}
                    </Typography>
                );
            }
            return (
                <Typography
                    variant="caption"
                    sx={tableCellSx}
                >
                    -
                </Typography>
            );
        },
    },
];